import { getCurrentSeason } from "@/lib/date-utils"

export type TimelineUrlOptions = {
  zipCode: string
  grassType?: string | null
  savedAnalysisId?: string | null
  resultAccessToken?: string | null
  lawnId?: string | null
  uploadedImageUrl?: string | null
  /** The visitor gave their email on the result (marks the plan as captured). */
  emailCaptured?: boolean
  season?: ReturnType<typeof getCurrentSeason>
}

/** Query flag for an email-captured visitor. The address never goes in a URL. */
export const TIMELINE_CAPTURED_PARAM = "captured"
/** Fragment key for the result access token: a fragment is never sent to the
 *  server, to referrers, or in the callback `next` of an OAuth hop. */
export const TIMELINE_ACCESS_FRAGMENT_KEY = "access"

/**
 * Builds the canonical timeline URL for homepage/result handoffs.
 * Keeping this centralized prevents query params and encoding rules from
 * drifting between anonymous, authenticated, and OAuth-return flows.
 *
 * The query carries only what the page renders from (ZIP, grass, season,
 * photo, row id, lawn). Secrets stay out of it: no email, and the result
 * access token rides in the fragment.
 */
export function buildTimelineUrl(options: TimelineUrlOptions): string {
  const params = new URLSearchParams()

  params.set("zipCode", options.zipCode)
  if (options.grassType) params.set("grassType", options.grassType)
  if (options.emailCaptured) params.set(TIMELINE_CAPTURED_PARAM, "1")
  params.set("season", options.season ?? getCurrentSeason())
  if (options.uploadedImageUrl) params.set("imageUrl", options.uploadedImageUrl)
  if (options.savedAnalysisId) params.set("analysisId", options.savedAnalysisId)
  if (options.lawnId) params.set("lawnId", options.lawnId)

  const fragment = options.resultAccessToken
    ? `#${new URLSearchParams({ [TIMELINE_ACCESS_FRAGMENT_KEY]: options.resultAccessToken }).toString()}`
    : ""

  return `/timeline?${params.toString()}${fragment}`
}
